/**
 * PAGE: ADMIN — Gestion des tarifs (TARIF-01).
 *
 * Grille tarifaire par édition (table pricing_items, 00153) : items
 * obligatoires (entrent dans le solde BED) + options facultatives (catalogue
 * seulement, hors solde — souscription par équipage = lot BED-14).
 *
 * Garde-fous :
 * - « Confirmer la grille » (RPC confirm_product_pricing) débloque l'émission
 *   BED ; toute écriture d'un item obligatoire re-passe l'édition en Brouillon
 *   (trigger DB trg_pricing_items_unconfirm) → re-fetch du produit après
 *   chaque mutation pour que le badge ne mente jamais.
 * - Écriture d'un item obligatoire avec des dossiers BED ouverts : avertissement
 *   avec le count — les liens émis conservent leur montant (jamais de révocation
 *   automatique, régénération par dossier via l'action existante BED-15).
 *
 * Sécurité réelle = RLS pricing_items (écritures superadmin) + RPC (42501).
 * Le guard usePermissions ci-dessous est purement UX (3 sites synchronisés :
 * Router requiredRole, canAccessRoute, nav config).
 */

import { useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { PageHeader, PageLayout } from '../components/page';
import { PricingItemModal } from '../components/modals/PricingItemModal';
import { Button, ConfirmDialog, ErrorState, LoadingState, toast } from '../components/ui';
import { RadixSelect } from '../components/ui/radix-select';
import { useSupabase } from '../hooks/useSupabase';
import { usePermissions } from '../hooks/usePermissions';
import { usePricingItems } from '../hooks/usePricingItems';
import { usePricingProducts } from '../hooks/usePricingProducts';
import { eurosToCents, formatCentsToEuros } from '../lib/pricing/money';
import type { PricingItemFormValues } from '../schemas/pricingItem';
import type { PricingItem, PricingItemKind } from '../types/pricingItem';

const BREADCRUMBS = [{ label: 'Admin' }, { label: 'Gestion des tarifs' }];

interface ModalState {
  kind: PricingItemKind;
  item: PricingItem | null;
}

interface PendingWarning {
  openBedCount: number;
  run: () => Promise<void>;
}

export function PricingPage() {
  const supabase = useSupabase();
  const { role } = usePermissions();
  const {
    products,
    loading: productsLoading,
    error: productsError,
    refresh: refreshProducts,
  } = usePricingProducts();
  const {
    pricingItems,
    loading: itemsLoading,
    error: itemsError,
    refresh: refreshItems,
    create,
    update,
    remove,
  } = usePricingItems();

  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [modalState, setModalState] = useState<ModalState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PricingItem | null>(null);
  const [pendingWarning, setPendingWarning] = useState<PendingWarning | null>(null);
  const [confirming, setConfirming] = useState(false);

  const selectedProduct = products.find((p) => p.id === selectedProductId) ?? null;

  const productItems = useMemo(
    () => pricingItems.filter((item) => item.product_id === selectedProductId && item.active),
    [pricingItems, selectedProductId]
  );
  const mandatoryItems = productItems.filter((item) => item.kind === 'mandatory');
  const optionalItems = productItems.filter((item) => item.kind === 'optional');
  // Affichage seulement — l'autorité pour le BED reste bed_solde_cents (DB).
  const mandatoryTotalCents = mandatoryItems.reduce((sum, item) => sum + item.amount_cents, 0);

  if (role !== 'superadmin') {
    return (
      <PageLayout header={<PageHeader breadcrumbs={BREADCRUMBS} />}>
        <ErrorState
          title="Accès réservé"
          message="Cette page est réservée aux superadmins."
          onRetry={() => window.location.reload()}
        />
      </PageLayout>
    );
  }

  if (productsLoading || itemsLoading) {
    return (
      <PageLayout header={<PageHeader breadcrumbs={BREADCRUMBS} />}>
        <LoadingState message="Chargement de la grille tarifaire..." />
      </PageLayout>
    );
  }

  if (productsError || itemsError) {
    return (
      <PageLayout header={<PageHeader breadcrumbs={BREADCRUMBS} />}>
        <ErrorState
          title="Erreur lors du chargement"
          message={(productsError ?? itemsError)?.message ?? 'Erreur inconnue'}
          onRetry={() => window.location.reload()}
        />
      </PageLayout>
    );
  }

  // Le trigger DB a pu déconfirmer : après TOUTE mutation d'item, on re-fetch
  // items ET produits pour que le badge Brouillon/Confirmée soit exact.
  async function refreshAll() {
    await Promise.all([refreshItems(), refreshProducts()]);
  }

  /** null = vérification impossible (erreur Supabase) — l'appelant doit bloquer. */
  async function countOpenBedCases(productId: string): Promise<number | null> {
    const { count, error } = await supabase
      .from('payment_cases')
      .select('id', { count: 'exact', head: true })
      .eq('scope', 'bed')
      .eq('product_id', productId)
      .not('status', 'in', '(paid,expired,failed)');
    if (error) return null;
    return count ?? 0;
  }

  /**
   * Écriture d'un item OBLIGATOIRE avec des BED ouverts sur l'édition :
   * avertissement d'abord (les liens émis gardent leur montant — invariant
   * D12/00144), sinon exécution directe. Fail-closed : si le count échoue,
   * on refuse la modification tarifaire plutôt que de la laisser passer
   * sans avertissement.
   */
  async function runWithOpenBedWarning(kind: PricingItemKind, run: () => Promise<void>) {
    if (kind === 'mandatory' && selectedProductId) {
      const openBedCount = await countOpenBedCases(selectedProductId);
      if (openBedCount === null) {
        toast.error(
          'Impossible de vérifier les dossiers BED ouverts — modification annulée, réessayez.'
        );
        return;
      }
      if (openBedCount > 0) {
        setPendingWarning({ openBedCount, run });
        return;
      }
    }
    await run();
  }

  async function handleModalSubmit(values: PricingItemFormValues) {
    if (!modalState || !selectedProductId) return;
    const { kind, item } = modalState;
    const payload = {
      label: values.label,
      code: values.code ? values.code : null,
      amount_cents: eurosToCents(values.amount_euros),
      conditions_url: values.conditions_url ? values.conditions_url : null,
    };

    await runWithOpenBedWarning(kind, async () => {
      if (item) {
        const ok = await update(item.id, payload);
        if (!ok) {
          toast.error("Échec de la modification de l'item.");
          return;
        }
        toast.success('Item modifié.');
      } else {
        const created = await create({
          product_id: selectedProductId,
          kind,
          sort_order: (kind === 'mandatory' ? mandatoryItems : optionalItems).length,
          ...payload,
        });
        if (!created) {
          toast.error(
            "Échec de l'ajout de l'item (libellé ou code déjà utilisé sur cette édition ?)."
          );
          return;
        }
        toast.success('Item ajouté.');
      }
      await refreshAll();
    });
  }

  async function handleDeleteConfirmed(item: PricingItem) {
    await runWithOpenBedWarning(item.kind, async () => {
      // Un item souscrit (payment_case_options) OU certifié
      // (payment_case_acknowledgments — seul chemin pour les mandatory, qui
      // ne passent jamais par les souscriptions) est protégé par FK (00164) :
      // on le DÉSACTIVE (retiré du catalogue, snapshots/preuves conservés)
      // au lieu de le supprimer.
      // Fail-closed : vérification impossible => on ne touche à rien.
      const [subscriptions, acknowledgments] = await Promise.all([
        supabase
          .from('payment_case_options')
          .select('id', { count: 'exact', head: true })
          .eq('pricing_item_id', item.id),
        supabase
          .from('payment_case_acknowledgments')
          .select('id', { count: 'exact', head: true })
          .eq('pricing_item_id', item.id),
      ]);
      if (subscriptions.error || acknowledgments.error) {
        toast.error(
          'Impossible de vérifier les souscriptions et certifications de cet item — suppression annulée.'
        );
        return;
      }
      if ((subscriptions.count ?? 0) > 0 || (acknowledgments.count ?? 0) > 0) {
        const deactivated = await update(item.id, { active: false });
        if (!deactivated) {
          toast.error("Échec de la désactivation de l'item.");
          return;
        }
        toast.success(
          'Item souscrit ou certifié par des dossiers BED : désactivé (retiré du catalogue), les dossiers existants conservent leur montant et leurs preuves.'
        );
        await refreshAll();
        return;
      }
      const ok = await remove(item.id);
      if (!ok) {
        toast.error("Échec de la suppression de l'item.");
        return;
      }
      toast.success('Item supprimé.');
      await refreshAll();
    });
  }

  async function handleConfirmPricing() {
    if (!selectedProductId) return;
    setConfirming(true);
    try {
      const { error } = await supabase.rpc('confirm_product_pricing', {
        p_product_id: selectedProductId,
      });
      if (error) {
        if (error.message.includes('pricing_no_mandatory_items')) {
          toast.error('Aucun item obligatoire actif : rien à confirmer.');
        } else if (error.code === '42501' || error.message.includes('superadmin')) {
          toast.error('Accès refusé (superadmin requis).');
        } else {
          toast.error('Erreur lors de la confirmation de la grille.');
        }
        return;
      }
      toast.success('Grille confirmée — le BED est émissible pour cette édition.');
      await refreshProducts();
    } finally {
      setConfirming(false);
    }
  }

  function ItemRow({ item }: { item: PricingItem }) {
    return (
      <div className="flex items-center justify-between rounded-md border border-zinc-200 px-4 py-2.5">
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-medium text-zinc-900">{item.label}</span>
          {item.code && <span className="text-xs text-zinc-500">{item.code}</span>}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className="text-sm tabular-nums text-zinc-900">
            {formatCentsToEuros(item.amount_cents)}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setModalState({ kind: item.kind, item })}
            aria-label={`Modifier ${item.label}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeleteTarget(item)}
            aria-label={`Supprimer ${item.label}`}
          >
            <Trash2 className="h-4 w-4 text-red-600" />
          </Button>
        </div>
      </div>
    );
  }

  const confirmedAtLabel = selectedProduct?.pricing_confirmed_at
    ? new Date(selectedProduct.pricing_confirmed_at).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <PageLayout header={<PageHeader breadcrumbs={BREADCRUMBS} />}>
      <div className="h-px w-full bg-zinc-200" />

      <div className="flex flex-col gap-6 p-6">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">Gestion des tarifs</h1>
          <p className="mt-1 text-sm text-zinc-600">
            Grille tarifaire par édition : les items obligatoires composent le solde BED, les
            options facultatives sont un catalogue (hors solde). L'émission de liens BED n'est
            possible que sur une grille confirmée.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="w-72">
            <RadixSelect
              options={products.map((p) => ({ value: p.id, label: p.name }))}
              placeholder="Choisir une édition"
              value={selectedProductId || undefined}
              onChange={setSelectedProductId}
            />
          </div>
          {selectedProduct &&
            (selectedProduct.pricing_confirmed ? (
              <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800">
                Confirmée{confirmedAtLabel ? ` le ${confirmedAtLabel}` : ''}
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-zinc-100 px-3 py-1 text-xs font-medium text-zinc-600">
                Brouillon — BED non émissible
              </span>
            ))}
        </div>

        {selectedProduct && (
          <>
            <section className="flex flex-col gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
                Obligatoires
              </h2>
              {mandatoryItems.length === 0 && (
                <p className="text-sm text-zinc-500">
                  Aucun item obligatoire — la grille ne peut pas être confirmée.
                </p>
              )}
              {mandatoryItems.map((item) => (
                <ItemRow key={item.id} item={item} />
              ))}
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setModalState({ kind: 'mandatory', item: null })}
                >
                  <Plus className="mr-1 h-4 w-4" /> Ajouter un item
                </Button>
              </div>
              <div className="mt-2 flex items-center justify-between border-t border-zinc-200 pt-3">
                <span className="text-sm font-semibold text-zinc-900">Total facturable (BED)</span>
                <span className="text-sm font-semibold tabular-nums text-zinc-900">
                  {formatCentsToEuros(mandatoryTotalCents)}
                </span>
              </div>
            </section>

            <section className="flex flex-col gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
                Options facultatives{' '}
                <span className="font-normal normal-case">(catalogue — hors solde BED)</span>
              </h2>
              {optionalItems.length === 0 && (
                <p className="text-sm text-zinc-500">Aucune option pour cette édition.</p>
              )}
              {optionalItems.map((item) => (
                <ItemRow key={item.id} item={item} />
              ))}
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setModalState({ kind: 'optional', item: null })}
                >
                  <Plus className="mr-1 h-4 w-4" /> Ajouter une option
                </Button>
              </div>
            </section>

            <div className="border-t border-zinc-200 pt-4">
              <Button
                onClick={handleConfirmPricing}
                disabled={
                  confirming || selectedProduct.pricing_confirmed || mandatoryItems.length === 0
                }
              >
                {selectedProduct.pricing_confirmed ? 'Grille confirmée' : 'Confirmer la grille'}
              </Button>
              {!selectedProduct.pricing_confirmed && (
                <p className="mt-2 text-xs text-zinc-500">
                  La confirmation fige la grille pour l'émission BED. Toute modification ultérieure
                  d'un item obligatoire repassera l'édition en brouillon.
                </p>
              )}
            </div>
          </>
        )}

        {!selectedProduct && (
          <p className="text-sm text-zinc-500">Choisissez une édition pour gérer sa grille.</p>
        )}
      </div>

      {modalState && (
        <PricingItemModal
          isOpen
          onClose={() => setModalState(null)}
          kind={modalState.kind}
          item={modalState.item}
          onSubmit={handleModalSubmit}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Supprimer cet item ?"
        description={
          deleteTarget
            ? `« ${deleteTarget.label} » (${formatCentsToEuros(deleteTarget.amount_cents)}) sera définitivement supprimé de la grille.`
            : undefined
        }
        confirmLabel="Supprimer"
        variant="destructive"
        onConfirm={() => {
          if (deleteTarget) {
            void handleDeleteConfirmed(deleteTarget);
            setDeleteTarget(null);
          }
        }}
      />

      <ConfirmDialog
        open={pendingWarning !== null}
        onOpenChange={(open) => {
          if (!open) setPendingWarning(null);
        }}
        title="Des dossiers BED sont ouverts sur cette édition"
        description={
          pendingWarning
            ? `${pendingWarning.openBedCount} dossier(s) BED ouvert(s) conservent leur montant émis — le nouveau tarif ne s'appliquera qu'aux prochains liens. Régénérer un lien (fiche équipage) ne rafraîchit le montant que d'un dossier sans encaissement : un dossier avec paiement en cours ou partiel garde son montant émis. Continuer ?`
            : undefined
        }
        confirmLabel="Continuer"
        onConfirm={() => {
          if (pendingWarning) {
            void pendingWarning.run();
            setPendingWarning(null);
          }
        }}
      />
    </PageLayout>
  );
}
